import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface UserProfile {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'staff' | 'client' | 'admin' | 'limited_access';
  phoneNumber?: string;
  addressString?: string | null;
  photoURL?: string;
  notificationsEnabled?: boolean;
  ndisNumber?: string;
  billingContactId?: string;
  billingContactName?: string;
  planManagerEligibility?: string;
  planStartDate?: string;
  planEndDate?: string;
  currentPlanStatus?: 'active' | 'inactive' | 'pending' | 'expired';
  plans?: {
    startDate: string;
    endDate: string;
    status: 'active' | 'inactive' | 'pending' | 'expired' | 'current';
    ndisNumber?: string;
  }[];
  supportCategories?: string;
  supportCoordinatorIds?: string[];
  supportCoordinatorNames?: string[];
  isSuperAdmin?: boolean;
  [key: string]: any;
}

interface ProfileStoreState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  unsubscribe: (() => void) | null;
  currentUserId: string | null;
}

interface ProfileStoreActions {
  subscribeToProfile: (userId: string) => Promise<void>;
  clearProfile: () => void;
}

// Module-level variable to track which user we've loaded
let activeUserId: string | null = null;

export const useProfileStore = create<ProfileStoreState & ProfileStoreActions>()(
  persist(
    (set, get) => ({
      // Initial state
      profile: null,
      isLoading: false,
      error: null,
      unsubscribe: null,
      currentUserId: null,

      // Action to load user profile from backend API
      // NOTE: Using API only, no Firestore listeners to avoid "Target ID already exists" error
      subscribeToProfile: async (userId) => {
        console.log(`🔍 ProfileStore: subscribeToProfile called for user ${userId}`);
        
        // If we already have this user's profile, skip
        if (activeUserId === userId && get().profile) {
          console.log(`✅ ProfileStore: Already have profile for user ${userId}, skipping`);
          return;
        }
        
        activeUserId = userId;
        set({ isLoading: true, error: null, currentUserId: userId });
        
        try {
          const response = await fetch(`${window.location.origin}/api/debug/user/${userId}`);
          const data = await response.json();
          
          if (data.exists && data.data) {
            console.log(`✅ ProfileStore: Profile loaded from API for user ${userId}`);
            set({ profile: data.data as UserProfile, isLoading: false, error: null });
          } else {
            console.error(`❌ ProfileStore: User profile not found for ${userId}`);
            set({ profile: null, isLoading: false, error: "User profile not found." });
          }
        } catch (err) {
          console.error(`❌ ProfileStore: Error fetching profile from API:`, err);
          set({ profile: null, isLoading: false, error: "Failed to load profile data." });
        }
      },

      // Action to clear profile data
      clearProfile: () => {
        console.log('🧹 ProfileStore: clearProfile called');
        activeUserId = null;
        set({ profile: null, isLoading: false, error: null, unsubscribe: null, currentUserId: null });
        console.log('✅ ProfileStore: Profile cleared');
      },
    }),
    {
      name: 'tapkonnect-profile-storage',
      partialize: (state) => ({ 
        profile: state.profile && state.profile.firstName ? state.profile : null,
      }),
    }
  )
);
